/**
 * Her payments, and anything she has questioned about them.
 *
 * Until now a mother could not raise a dispute as herself. The record supported
 * it from the start, but the only way in was the partner API — so she could
 * query her own money only by asking Ohai to do it for her. For a mother whose
 * money it is, that was the wrong shape, and this is the screen that fixes it.
 *
 * WRITTEN TO HER, NOT ABOUT HER. The partner-facing copy describes her in the
 * third person because Ohai's staff are reading it. Here she is the reader, so
 * everything is "your payment", "we will come back to you". The same facts; a
 * different person is being spoken to.
 *
 * THE QUERY OPENS FROM THE PAYMENT. A dispute needs a payout id and asking her
 * to find one would be asking her to do the system's filing.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertCircle, Check, CircleDollarSign } from 'lucide-react';

interface Payout {
  id: number;
  payout_amount: number | string | null;
  status: string | null;
  payout_date: string | null;
  what_for: string | null;
}

interface Dispute {
  dispute_id: number;
  dispute_number: string | null;
  dispute_type: string;
  status: string;
  raised_at: string;
  resolve_due_at: string | null;
  sla_breached: boolean;
  determination: string | null;
  explanation: string | null;
  payout_id: number | null;
  payout_amount: number | null;
  what_for: string | null;
}

/** What a MEMBER may say is wrong. Her list differs from a doula's: she disputes
 *  a payout, so 'eligibility' and 'evidence' apply and 'denial'/'delay' do not. */
const TYPES = ['eligibility', 'evidence', 'amount', 'non_receipt', 'unauthorized'] as const;

function when(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function money(v: number | string | null): string {
  if (v === null || v === undefined) return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isNaN(n) ? '—' : `$${n.toFixed(2)}`;
}

export default function MemberDisputesPage() {
  const { t } = useTranslation();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [openFor, setOpenFor] = useState<number | null>(null);
  const [type, setType] = useState<string>('amount');
  const [statement, setStatement] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, d] = await Promise.all([
        authFetchJson<{ payouts: Payout[] }>('/api/member-portal/payouts'),
        authFetchJson<{ disputes: Dispute[] }>('/api/member-portal/disputes'),
      ]);
      setPayouts(p.payouts ?? []);
      setDisputes(d.disputes ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('memberDisputes.loadFailed', 'We could not load your payments.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const raise = async (payoutId: number) => {
    setBusy(true);
    setError(null);
    try {
      await authFetchJson('/api/member-portal/disputes', {
        method: 'POST',
        body: JSON.stringify({
          payout_id: payoutId,
          dispute_type: type,
          statement: statement.trim() || null,
        }),
      });
      setDone(t('memberDisputes.raised', 'We have this. Someone will look at it and come back to you.'));
      setOpenFor(null);
      setStatement('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('memberDisputes.failed', 'We could not send your question.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PageState variant="loading" />;

  const queried = new Set(disputes.map((d) => d.payout_id));

  return (
    <PageContainer>
      <PageHeader
        title={t('memberDisputes.title', 'My payments')}
        description={t(
          'memberDisputes.description',
          'What you have been paid, and anything you have asked us about.',
        )}
      />

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {done && (
        <div className="mb-4 flex items-start gap-2 rounded-md border p-4 text-sm">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{done}</span>
        </div>
      )}

      {payouts.length === 0 ? (
        <div className="rounded-md border p-8 text-center">
          <CircleDollarSign className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">
            {t('memberDisputes.noPayments', 'You have no payments yet.')}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {payouts.map((p) => (
            <li key={p.id} className="rounded-md border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{money(p.payout_amount)}</p>
                  {/* what_for is already plain language from B8 — it exists so
                      she never has to decode a programme code. */}
                  {p.what_for && <p className="mt-0.5 text-sm text-muted-foreground">{p.what_for}</p>}
                  <p className="mt-0.5 text-sm text-muted-foreground">{when(p.payout_date)}</p>
                </div>
                <Badge variant={p.status === 'paid' ? 'success' : 'secondary'}>
                  {p.status === 'paid'
                    ? t('memberDisputes.paid', 'Paid')
                    : t('memberDisputes.onItsWay', 'On its way')}
                </Badge>
              </div>

              {openFor === p.id ? (
                <div className="mt-4 space-y-3 border-t pt-3">
                  <div>
                    <Label htmlFor={`t-${p.id}`}>
                      {t('memberDisputes.whatIsWrong', 'What would you like to ask about?')}
                    </Label>
                    <select
                      id={`t-${p.id}`}
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                    >
                      {TYPES.map((v) => (
                        <option key={v} value={v}>
                          {t(`memberDisputes.type.${v}`, v.replace(/_/g, ' '))}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Textarea
                    value={statement}
                    onChange={(e) => setStatement(e.target.value)}
                    rows={3}
                    placeholder={t('memberDisputes.placeholder', 'Tell us what happened — optional.')}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" disabled={busy} onClick={() => void raise(p.id)}>
                      {t('memberDisputes.send', 'Send')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setOpenFor(null)}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                !queried.has(p.id) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setOpenFor(p.id);
                      setType('amount');
                    }}
                  >
                    {t('memberDisputes.ask', 'Ask about this payment')}
                  </Button>
                )
              )}
            </li>
          ))}
        </ul>
      )}

      {disputes.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('memberDisputes.yourQuestions', 'What you have asked us')}
          </h2>
          <ul className="space-y-3">
            {disputes.map((d) => (
              <li key={d.dispute_id} className="rounded-md border p-4 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {t(`memberDisputes.type.${d.dispute_type}`, d.dispute_type.replace(/_/g, ' '))}
                  </span>
                  <Badge variant={d.sla_breached ? 'danger' : 'secondary'}>{d.status}</Badge>
                  {d.payout_amount !== null && (
                    <span className="text-muted-foreground">{money(d.payout_amount)}</span>
                  )}
                </div>
                <p className="mt-1 text-muted-foreground">
                  {t('memberDisputes.askedOn', 'asked {{date}}', { date: when(d.raised_at) })}
                  {d.resolve_due_at
                    ? ` · ${t('memberDisputes.answerBy', 'we will answer by {{date}}', { date: when(d.resolve_due_at) })}`
                    : ''}
                </p>
                {d.sla_breached && (
                  <p className="mt-1 text-red-700">
                    {t('memberDisputes.overdue', 'We are past the date we promised you an answer. We are sorry.')}
                  </p>
                )}
                {d.explanation && <p className="mt-2">{d.explanation}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </PageContainer>
  );
}
